import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Search, Plus, Link, Upload } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Helper function to format currency
const formatCurrency = (amount: number, currency = 'EUR') => {
  return new Intl.NumberFormat('el-GR', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

export const BankTransactionsList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [accountFilter, setAccountFilter] = useState<string>('all');
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Load bank accounts for filter
  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bank_accounts')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      return data;
    }
  });

  // Load bank transactions
  const { data: transactions, isLoading, refetch } = useQuery({
    queryKey: ['bank-transactions', searchTerm, statusFilter, accountFilter],
    queryFn: async () => {
      let query = supabase
        .from('bank_transactions')
        .select(`
          *,
          bank_accounts:bank_account_id (
            name,
            currency
          ),
          payments:payment_id (
            id,
            reference,
            direction
          )
        `)
        .order('date', { ascending: false });

      if (searchTerm) {
        query = query.ilike('description', `%${searchTerm}%`);
      }

      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (accountFilter && accountFilter !== 'all') {
        query = query.eq('bank_account_id', accountFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    }
  });

  // Load unmatched payments for matching
  const { data: unmatchedPayments } = useQuery({
    queryKey: ['unmatched-payments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .is('bank_account_id', null) // Payments not yet matched to bank transactions
        .order('date', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      return data;
    }
  });

  // Match transaction to existing payment
  const matchPayment = useMutation({
    mutationFn: async ({ transactionId, paymentId }: { transactionId: string; paymentId: string }) => {
      // Update the bank transaction
      const { error: transactionError } = await supabase
        .from('bank_transactions')
        .update({ 
          status: 'matched',
          payment_id: paymentId
        })
        .eq('id', transactionId);

      if (transactionError) throw transactionError;

      // Update the payment with bank account reference
      const { error: paymentError } = await supabase
        .from('payments')
        .update({ 
          bank_account_id: selectedTransaction?.bank_account_id
        })
        .eq('id', paymentId);

      if (paymentError) throw paymentError;
    },
    onSuccess: () => {
      toast({ title: "Transaction matched successfully" });
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['unmatched-payments'] });
      setMatchDialogOpen(false);
      setSelectedTransaction(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error matching transaction",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Create new payment from transaction
  const createPayment = useMutation({
    mutationFn: async ({ transactionId, amount, direction }: { 
      transactionId: string; 
      amount: number; 
      direction: 'IN' | 'OUT';
    }) => {
      // Create new payment
      const { data: payment, error: paymentError } = await supabase
        .from('payments')
        .insert([{
          amount: Math.abs(amount),
          direction,
          date: selectedTransaction?.date,
          reference: `Bank Transaction: ${selectedTransaction?.description}`,
          method: 'BANK_TRANSFER',
          bank_account_id: selectedTransaction?.bank_account_id
        }])
        .select()
        .single();

      if (paymentError) throw paymentError;

      // Update the bank transaction
      const { error: transactionError } = await supabase
        .from('bank_transactions')
        .update({ 
          status: 'matched',
          payment_id: payment.id
        })
        .eq('id', transactionId);

      if (transactionError) throw transactionError;

      // Create GL posting for the payment
      const { error: glError } = await supabase.rpc('create_payment_gl_posting', {
        payment_id_param: payment.id
      });

      if (glError) throw glError;

      return payment;
    },
    onSuccess: () => {
      toast({ title: "Payment created successfully" });
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      setMatchDialogOpen(false);
      setSelectedTransaction(null);
      setPaymentAmount('');
    },
    onError: (error: any) => {
      toast({
        title: "Error creating payment",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleMatch = (transaction: any) => {
    setSelectedTransaction(transaction);
    setPaymentAmount(Math.abs(transaction.amount).toString());
    setMatchDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'matched': return 'default';
      case 'unmatched': return 'secondary';
      case 'ignored': return 'outline';
      default: return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Bank Transactions</h1>
        <Button variant="outline">
          <Upload className="h-4 w-4 mr-2" />
          Import CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="unmatched">Unmatched</SelectItem>
                <SelectItem value="matched">Matched</SelectItem>
                <SelectItem value="ignored">Ignored</SelectItem>
              </SelectContent>
            </Select>
            <Select value={accountFilter} onValueChange={setAccountFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {bankAccounts?.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading transactions...</div>
          ) : transactions && transactions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Matched Payment</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      {format(new Date(transaction.date), "MMM dd, yyyy")}
                    </TableCell>
                    <TableCell className="font-medium max-w-xs truncate">
                      {transaction.description}
                    </TableCell>
                    <TableCell>
                      {transaction.bank_accounts?.name}
                    </TableCell>
                    <TableCell className={`text-right font-semibold ${
                      transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(transaction.amount, transaction.bank_accounts?.currency)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusColor(transaction.status)}>
                        {transaction.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {transaction.payments ? (
                        <div className="text-sm">
                          <div>{transaction.payments.reference}</div>
                          <div className="text-muted-foreground">
                            {transaction.payments.direction} • ID: {transaction.payments.id.slice(-8)}
                          </div>
                        </div>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {transaction.status === 'unmatched' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMatch(transaction)}
                        >
                          <Link className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No bank transactions found. Import your bank statement to get started.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Match/Create Payment Dialog */}
      <Dialog open={matchDialogOpen} onOpenChange={setMatchDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Match Transaction</DialogTitle>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded">
                <div className="text-sm font-medium">{selectedTransaction.description}</div>
                <div className="text-sm text-muted-foreground">
                  {format(new Date(selectedTransaction.date), "MMM dd, yyyy")} • 
                  {formatCurrency(selectedTransaction.amount, selectedTransaction.bank_accounts?.currency)}
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium">Match to existing payment:</h4>
                {unmatchedPayments && unmatchedPayments.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {unmatchedPayments
                      .filter(p => Math.abs(p.amount - Math.abs(selectedTransaction.amount)) < 0.01)
                      .map((payment) => (
                        <div
                          key={payment.id}
                          className="p-2 border rounded cursor-pointer hover:bg-muted"
                          onClick={() => matchPayment.mutate({ 
                            transactionId: selectedTransaction.id,
                            paymentId: payment.id
                          })}
                        >
                          <div className="text-sm font-medium">{payment.reference}</div>
                          <div className="text-sm text-muted-foreground">
                            {payment.direction} • {formatCurrency(payment.amount)}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No matching payments found</p>
                )}

                <div className="border-t pt-3">
                  <h4 className="font-medium mb-2">Or create new payment:</h4>
                  <div className="space-y-2">
                    <div className="space-y-1">
                      <Label htmlFor="payment-amount">Amount</Label>
                      <Input
                        id="payment-amount"
                        type="number"
                        step="0.01"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                      />
                    </div>
                    <Button 
                      className="w-full"
                      onClick={() => createPayment.mutate({
                        transactionId: selectedTransaction.id,
                        amount: parseFloat(paymentAmount),
                        direction: selectedTransaction.amount >= 0 ? 'IN' : 'OUT'
                      })}
                      disabled={!paymentAmount || createPayment.isPending}
                    >
                      {createPayment.isPending ? 'Creating...' : 'Create Payment'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};